
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doctors } from '../data';
import { Doctor } from '../types';

const fadeInUp = {
  hidden: { opacity: 0, y: 40, filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const Team = () => {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);

  const specialties = ['All'];

  const filteredDoctors = doctors.filter(doc => {
    const matchesFilter = filter === 'All' || filter === 'All Specialties' || doc.category === filter;
    const matchesSearch = doc.name.toLowerCase().includes(search.toLowerCase()) ||
      doc.role.toLowerCase().includes(search.toLowerCase()) ||
      doc.expertise.some(e => e.toLowerCase().includes(search.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedDoctor(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <main className="font-geist text-primary bg-soft-gray min-h-screen">

      {/* Hero Header */}
      <section className="relative min-h-[85vh] flex items-center pt-32 pb-20 overflow-hidden bg-slate-900">
        {/* Background Image - Converted to IMG tag for LCP optimization */}
        <div className="absolute inset-0">
          <img
            src="https://ik.imagekit.io/6dlukcyok/Medical%20bucket/Group.avif"
            alt="Medical Team Background"
            className="w-full h-full object-cover object-center opacity-70"
            loading="eager"
            fetchPriority="high"
          />
        </div>

        {/* Lighter Gradient Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/30"></div>

        <div className="relative z-10 w-full px-6 md:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="max-w-4xl pt-10"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
              }}
            >
              <motion.div variants={fadeInUp} className="hidden md:inline-flex items-center gap-3 py-3 px-6 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-bold uppercase tracking-[0.2em] mb-12">
                <span className="material-symbols-outlined text-base">medical_services</span>
                Medical Excellence
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-white text-4xl md:text-6xl lg:text-7xl font-sans-jakarta font-extrabold leading-[1.1] tracking-tight mb-10 drop-shadow-xl">
                Experienced. <br />
                Approachable. <br />
                <span className="text-teal-400 italic">Professional</span>.
              </motion.h1>

              <motion.div variants={fadeInUp} className="space-y-8">
                <div className="border-l-2 border-teal-400/50 pl-8">
                  <p className="text-white/95 text-lg md:text-xl font-light leading-relaxed max-w-2xl drop-shadow-md">
                    Led by Dr CG Du Plessis and Dr LL Kanagaratnam, our team delivers high-quality medical care with absolute respect and total discretion.
                  </p>
                </div>

                <p className="text-white/70 text-base md:text-lg font-light max-w-xl leading-relaxed">
                  Focusing on long-term relationships and clear communication, ensuring your care adapts to your unique journey.
                </p>

                <p className="text-white text-lg md:text-xl font-sans-jakarta font-medium pt-4 opacity-90">
                  "You are never just another appointment."
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Grid & Filters */}
      <section className="py-24 px-6 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Controls */}
          {/* Controls removed per client request - no specialty filters or search bar */}

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            <AnimatePresence mode="popLayout">
              {filteredDoctors.map((doc) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="group bg-white rounded-3xl overflow-hidden hover:shadow-[0_40px_80px_-20px_rgba(15,23,42,0.15)] transition-shadow duration-700 border border-slate-100 flex flex-col cursor-pointer transform-gpu"
                  onClick={() => setSelectedDoctor(doc)}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
                    <img
                      src={doc.image}
                      alt={doc.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500"></div>

                    {doc.badge && (
                      <span className="absolute top-6 right-6 bg-accent text-white text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full shadow-lg">
                        {doc.badge}
                      </span>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <p className="text-teal-400 font-bold text-[10px] uppercase tracking-[0.3em] mb-2">{doc.role}</p>
                      <h3 className="font-sans-jakarta text-2xl font-bold tracking-tight">{doc.name}</h3>
                    </div>
                  </div>

                  <div className="p-8 flex-grow flex flex-col">
                    <p className="text-slate-500 text-base leading-relaxed mb-6 flex-grow font-light line-clamp-4">
                      {doc.description}
                    </p>

                    <div className="flex items-center gap-2 text-accent font-bold text-xs uppercase tracking-widest mb-8 group-hover:gap-4 transition-all">
                      Read Full Bio <span className="material-symbols-outlined text-lg">arrow_forward</span>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-auto">
                      {doc.expertise.map((exp, i) => (
                        <span key={i} className="px-3 py-1.5 rounded-lg bg-slate-50 text-slate-500 text-[9px] font-bold uppercase tracking-widest border border-slate-100">
                          {exp}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {filteredDoctors.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-32 bg-white rounded-[3rem] shadow-sm mt-10"
            >
              <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="material-symbols-outlined text-4xl text-slate-300">person_search</span>
              </div>
              <p className="text-slate-500 text-xl font-light">No practitioners found matching your criteria.</p>
              <button
                onClick={() => { setFilter('All'); setSearch(''); }}
                className="mt-8 px-8 py-3 bg-primary text-white rounded-full font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-accent transition-colors"
              >
                Clear Search & Filters
              </button>
            </motion.div>
          )}

        </div>
      </section>

      {/* Doctor Bio Modal */}
      <AnimatePresence>
        {selectedDoctor && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setSelectedDoctor(null)}
            ></div>

            <motion.div
              className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh]"
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedDoctor(null)}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-slate-900 hover:bg-white transition-colors shadow-sm"
              >
                <span className="material-symbols-outlined">close</span>
              </button>

              {/* Image Section */}
              <div className="w-full md:w-5/12 relative bg-slate-100 min-h-[300px] md:min-h-full">
                <img
                  src={selectedDoctor.image}
                  alt={selectedDoctor.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent md:bg-gradient-to-r md:from-transparent md:to-transparent"></div>

                {selectedDoctor.badge && (
                  <span className="absolute top-6 left-6 bg-accent text-white text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full shadow-lg">
                    {selectedDoctor.badge}
                  </span>
                )}

                <div className="absolute bottom-6 left-6 text-white md:hidden">
                  <p className="text-teal-400 font-bold text-[10px] uppercase tracking-[0.3em] mb-1">{selectedDoctor.role}</p>
                  <h2 className="font-sans-jakarta text-2xl font-bold">{selectedDoctor.name}</h2>
                </div>
              </div>

              {/* Content Section */}
              <div className="w-full md:w-7/12 p-8 md:p-12 overflow-y-auto">
                <div className="hidden md:block mb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="h-0.5 w-8 bg-accent"></span>
                    <p className="text-accent font-bold text-xs uppercase tracking-[0.3em]">{selectedDoctor.role}</p>
                  </div>
                  <h2 className="font-sans-jakarta text-4xl lg:text-5xl font-bold text-primary mb-2">{selectedDoctor.name}</h2>
                  <p className="text-slate-400 text-sm font-medium uppercase tracking-widest">{selectedDoctor.subRole}</p>
                </div>

                <div className="prose prose-lg prose-slate max-w-none mb-10">
                  <p className="text-slate-600 font-light leading-relaxed">
                    {selectedDoctor.description}
                  </p>
                </div>

                <div className="mb-10">
                  <h4 className="font-sans-jakarta font-bold text-primary text-sm uppercase tracking-widest mb-4">Areas of Expertise</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDoctor.expertise.map((exp, i) => (
                      <span key={i} className="px-4 py-2 rounded-full bg-slate-50 text-slate-600 text-xs font-bold uppercase tracking-widest border border-slate-100">
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-slate-100 mt-auto">
                  <a
                    href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-accent text-white px-8 py-4 rounded-full text-center text-xs font-bold uppercase tracking-widest hover:bg-teal-700 transition-colors shadow-lg shadow-accent/20 flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-lg">calendar_month</span>
                    Book Appointment
                  </a>
                  <button
                    onClick={() => setSelectedDoctor(null)}
                    className="px-8 py-4 rounded-full text-center text-xs font-bold uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-colors border border-slate-200"
                  >
                    Close Profile
                  </button>
                </div>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </main>
  );
};

export default Team;
